import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo } from 'react';
import { useNextQueryParams } from '@hub/hooks/useNextQueryParams';
import type { FilterType, FilterValueUrlParser, ReturnValueForT } from '../types';

// converts string | string[] from url to filter value

type SetFn<FilterT extends FilterType> = (newValue: ReturnValueForT<FilterT> | undefined) => void;

export const useFreeFormHubFilterState = <FilterT extends FilterType>(
  urlParameter: string,
  validator: FilterValueUrlParser<FilterT>,
  resetPage?: boolean | undefined
): [ReturnValueForT<FilterT> | undefined, SetFn<FilterT>] => {
  const query = useNextQueryParams();
  const { replace } = useRouter();

  let value: ReturnValueForT<FilterT> | undefined;

  const urlValue = useMemo(() => query[urlParameter], [query, urlParameter]);

  if (typeof urlValue === 'string') {
    value = validator(urlValue);
  }

  const setValue = useCallback<SetFn<FilterT>>(
    newValue => {
      if (newValue && newValue.toString() === (urlValue as string)) {
        return;
      }
      const getNewQuery = () => {
        if (newValue === undefined) {
          delete query[urlParameter];
          return query;
        }

        const updateValue = Array.isArray(newValue) ? newValue.map(String).join(',') : newValue.toString();

        return { ...query, [urlParameter]: updateValue };
      };
      const newQuery = getNewQuery();
      // when resetPage is set, this means a change to this filter should result in resetting the page to the first page.
      // to avoid unnecessary rerenders or refetching of data from the server the page query param needs to be set along
      // with the updated filter query param in a single replace call, instead of subsequent calls.
      if (resetPage) {
        delete newQuery.params;
      }
      void replace({ query: newQuery }, undefined, { shallow: true });
    },
    [query, urlParameter, resetPage, replace]
  );

  useEffect(() => {
    if (value?.toString() !== (urlValue as string)) {
      setValue(value);
    }
  }, [value, urlValue, setValue]);

  return [value, setValue];
};
